import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Send, Loader2 } from 'lucide-react';

// Admin asks a supplier to revise their quote.
// At least one of: target_price_per_ct, target_price_total, notes must be set.
// On submit, posts to /stones/quotes/{id}/request-revision.
export default function ReviseQuoteDialog({ open, onOpenChange, quote, onSubmit }) {
  const [targetPerCt, setTargetPerCt] = useState('');
  const [targetTotal, setTargetTotal] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setTargetPerCt(''); setTargetTotal(''); setNotes(''); }
  }, [open]);

  const valid = targetPerCt.trim() || targetTotal.trim() || notes.trim();

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await onSubmit({
        target_price_per_ct: targetPerCt ? parseFloat(targetPerCt) : null,
        target_price_total: targetTotal ? parseFloat(targetTotal) : null,
        notes: notes.trim(),
      });
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  if (!quote) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request revision from {quote.supplier_name}</DialogTitle>
          <DialogDescription>
            Set a target price and/or message. The supplier will see this and can submit an updated quote.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs text-muted-foreground bg-slate-50 rounded p-2">
            <span className="font-medium text-slate-700">Current quote:</span>{' '}
            {quote.price_per_ct != null && <>{quote.currency} {quote.price_per_ct} / ct{' · '}</>}
            {quote.price_total != null && <>Total {quote.currency} {Number(quote.price_total).toLocaleString()}</>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Target price / ct</Label>
              <Input type="number" inputMode="decimal" step="0.01" value={targetPerCt}
                onChange={e => setTargetPerCt(e.target.value)}
                className="h-10 text-base mt-1"
                placeholder={`${quote.currency} ${quote.price_per_ct ?? '—'}`}
                data-testid="revise-target-per-ct" />
            </div>
            <div>
              <Label className="text-xs">Target total price</Label>
              <Input type="number" inputMode="decimal" step="0.01" value={targetTotal}
                onChange={e => setTargetTotal(e.target.value)}
                className="h-10 text-base mt-1"
                placeholder={`${quote.currency} ${quote.price_total ?? '—'}`}
                data-testid="revise-target-total" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Message to supplier</Label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full text-sm p-2 border rounded-md bg-slate-50 min-h-[80px] resize-y mt-1"
              placeholder="e.g. Please review your quality grade, we expect better clarity at this price."
              data-testid="revise-notes" />
          </div>
          <p className="text-[10px] text-muted-foreground">
            This will also post your message in the negotiation chat thread.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || saving} className="bg-violet-600 hover:bg-violet-700" data-testid="revise-submit">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
            Send revision request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
